/**
 * Cross-instance bookkeeping for the image variation hooks.
 *
 * The hooks below may be mounted more than once — a different wizard step, a
 * preview, a second view of the same project — so the work already in flight
 * is tracked here, per project, rather than per hook instance.
 */

/**
 * Labeling requests in flight, shared by every `useImageLabeling`: the same
 * reference image can belong to more than one group, and it only has to be
 * described once.
 */
export const loadingImageLabeling = new Set();

/** The project-scoped identity of an image inside `loadingImageLabeling`. */
export const labelingId = (projectId, key) => `${projectId}\u0000${key}`;

/**
 * Labeling results already received, per `labelingId`, with the file they
 * describe. A concurrent `assets` write can drop a saved labeling; it is then
 * restored from here instead of being requested again.
 */
export const imageLabelingResults = new Map();

export const hasLabelingFields = (labeling, fields) =>
  !!labeling &&
  typeof labeling === "object" &&
  !Array.isArray(labeling) &&
  fields.every(
    (field) =>
      typeof labeling[field] === "string" && labeling[field].trim().length > 0,
  );

/**
 * Prompt requests in flight, shared by every `useImagePrompts`: the same
 * variation can be rendered by more than one group, and its prompt is only
 * generated once — the entry is kept until the resulting `visualPrompt` has
 * been persisted, so the effect never asks for it twice.
 */
export const loadingImagePrompts = new Set();

/** The project-scoped identity of a variation inside `loadingImagePrompts`. */
export const promptId = (projectId, key) => `${projectId}\u0000${key}`;

/**
 * Persisting `imageVariations` rewrites the *whole* array, so two writes
 * started from the same snapshot drop each other's entries — and the prompt
 * effect would then ask for the lost prompts again, forever. Every write of a
 * project is chained here, across all hook instances, so each one reads the
 * cache only after the previous one has settled.
 */
const imageVariationsWrites = new Map();

export const queueImageVariationsWrite = (projectId, write) => {
  const previous = imageVariationsWrites.get(projectId) ?? Promise.resolve();
  const next = previous.then(write, write);

  // A failed write must not break the queue for the ones behind it.
  const settled = next.catch(() => {});

  imageVariationsWrites.set(projectId, settled);

  settled.then(() => {
    // Nothing else queued up behind it — stop tracking the idle chain.
    if (imageVariationsWrites.get(projectId) === settled) {
      imageVariationsWrites.delete(projectId);
    }
  });

  return next;
};
